import { Suspense } from "react";
import { Loader2 } from "lucide-react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getActiveChild } from "@/config/sidebar-nav";
import { useSidebarNavFeature } from "@/hooks/use-sidebar-nav-features";
import { useIsMobile } from "@/hooks/use-mobile";

const fallback = (
  <div className="flex justify-center py-12">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

type Props = {
  featureId: "teams" | "performance" | "routines" | "playbook" | "coach";
};

/** Groups whose pages draw their own heading; the layout then only frames them. */
const VIEW_OWNS_TITLE = new Set<Props["featureId"]>(["playbook", "routines", "coach"]);

/**
 * Chrome for a page inside a nav group: the page title, which is the active
 * sub-page's label (Team, Check in, Tester, …) since the shell's tab row
 * (`ShellSubTabs`) already shows the siblings. On phones a sub-page marked
 * `mobileFullScreen` (the pulse rating flow) renders bare, since it is a
 * full-screen flow of its own.
 *
 * The pulse page lives under `/coach/performance` but is a Team sub-page in
 * the nav, so the Team group titles it in both verticals.
 */
export default function CoachSectionLayout({ featureId }: Props) {
  const feature = useSidebarNavFeature(featureId);
  const teams = useSidebarNavFeature("teams");
  const { pathname } = useLocation();
  const { t } = useTranslation("nav");
  const isMobile = useIsMobile();

  const owner =
    teams && getActiveChild(teams, pathname) && pathname !== teams.url
      ? teams
      : feature;
  const activeTab = owner ? getActiveChild(owner, pathname) : undefined;
  const titleKey = activeTab?.titleKey ?? owner?.titleKey;

  if (
    !titleKey ||
    VIEW_OWNS_TITLE.has(featureId) ||
    (isMobile && activeTab?.mobileFullScreen)
  ) {
    return (
      <Suspense fallback={fallback}>
        <Outlet />
      </Suspense>
    );
  }

  return (
    <div className="min-w-0 space-y-6">
      <h1 className="text-[28px] font-extrabold tracking-[-0.02em] text-g-ink-900">
        {t(titleKey)}
      </h1>
      <Suspense fallback={fallback}>
        <Outlet />
      </Suspense>
    </div>
  );
}
